/**
 * Tokens — every device or script authenticated under this account (SPEC.md §2.2).
 *
 * Manual minting is gone (D201): `PairDeviceCard`, rendered directly above the list below,
 * is now the only way a new one comes into being. That form existed for a world with no
 * accounts at all, where the browser itself needed a bearer token to open this very page —
 * that stopped being true the moment sessions shipped (item 16), and it only ever grew more
 * pointless from there: any real client either logs in with its own account (the browser)
 * or pairs like a watch does, and a one-off script that genuinely needs its own token
 * (`colossus`'s GPU hooks, say) is a documented `curl` call in the README, not a form
 * littering this page with scope checkboxes nobody but the owner should ever touch.
 *
 * What's left here is purely informational and administrative: see what exists, see when
 * it was last used, revoke it.
 */

import { useCallback, useEffect, useState } from "react";
import { ApiError, api, type TokenInfo } from "../api";
import { impliedScopes } from "../scopes";
import { EyeToggle } from "./EyeToggle";
import { PairDeviceCard } from "./PairDeviceCard";

function when(epoch: number | null): string {
  if (!epoch) return "never";
  return new Date(epoch * 1000).toLocaleString();
}

export function TokensCard({
  refreshKey,
}: {
  /** Bumped whenever something elsewhere on Settings changed — no current sibling actually
   * needs this (pairing calls `load` directly now, being rendered right here), but every
   * other section on this page takes it, and there's no reason this one shouldn't be ready
   * for a future one that does. */
  refreshKey?: number;
} = {}) {
  const [tokens, setTokens] = useState<TokenInfo[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showRevoked, setShowRevoked] = useState(false);

  const load = useCallback(async (includeRevoked: boolean) => {
    try {
      setTokens((await api.tokens(includeRevoked)).tokens);
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.isAuth) return;
      setError(err instanceof Error ? err.message : String(err));
      setTokens([]);
    }
  }, []);

  useEffect(() => {
    void load(showRevoked);
  }, [load, refreshKey, showRevoked]);

  async function revoke(token: TokenInfo) {
    if (!window.confirm(`Revoke "${token.name}"? Any client using it stops working.`)) return;
    try {
      const { revoked_at } = await api.revokeToken(token.id);
      // Patched in place, not re-fetched: a fresh list would already exclude the row we
      // just revoked (gitea #49, D203), and it's meant to stay crossed out until an actual
      // page refresh, not vanish the instant you act on it.
      setTokens(
        (prev) => prev?.map((t) => (t.id === token.id ? { ...t, revoked_at } : t)) ?? prev,
      );
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <>
      <PairDeviceCard onChange={() => load(showRevoked)} />

      <section className="panel">
        <header>
          <h2>Tokens</h2>
          <span className="row">
            <span className="muted small">
              One unused for months is either dead or stolen - revoke it either way
            </span>
            <EyeToggle
              open={showRevoked}
              onToggle={() => setShowRevoked((v) => !v)}
              label="revoked tokens"
            />
          </span>
        </header>

        {error && <p className="error">{error}</p>}
        {tokens === null ? (
          <p className="muted">Loading…</p>
        ) : tokens.length === 0 ? (
          <p className="muted small">None yet - pair a device above to mint the first one.</p>
        ) : (
          <table className="tokens">
            <thead>
              <tr>
                <th>Name</th>
                <th>Scopes</th>
                <th>Last used</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {tokens.map((token) => (
                <tr key={token.id} className={token.revoked_at ? "revoked" : undefined}>
                  <td>{token.name}</td>
                  <td className="small">
                    {token.scopes.join(", ")}
                    {/* What it actually reaches, which is not always what was granted. */}
                    {impliedScopes(token.scopes).length > 0 && (
                      <span className="muted"> (+{impliedScopes(token.scopes).join(", ")})</span>
                    )}
                  </td>
                  <td className="small muted">{when(token.last_used_at)}</td>
                  <td>
                    {!token.revoked_at && (
                      <button className="btn ghost small" onClick={() => void revoke(token)}>
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}
